import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { regionImage, ownerDisplayName, ownerPhotoSrc } from "@/lib/regions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Calendar, UserRound, ArrowRight } from "lucide-react";

const ALL_INDIA_CITIES = [
  "Chandigarh Tricity", "Ahmedabad", "Delhi-NCR", "Mumbai", "Bengaluru", "Pune",
  "Hyderabad", "Chennai", "Kolkata", "Jaipur", "Lucknow", "Indore",
  "Surat", "Nagpur", "Coimbatore", "Kochi", "Vadodara", "Bhopal",
  "Ludhiana", "Visakhapatnam", "Goa",
];

function OwnerAvatar({ circle, size = "w-12 h-12" }) {
  return (
    <div className={`${size} rounded-full overflow-hidden bg-gray-100 ring-2 ring-gold/40 flex items-center justify-center flex-shrink-0`}>
      {circle.owner_photo_url
        ? <img src={ownerPhotoSrc(circle.owner_photo_url)} alt={ownerDisplayName(circle)} className="w-full h-full object-cover object-top" />
        : <UserRound size={20} className="text-navy/40" />}
    </div>
  );
}

function RegionCard({ circle }) {
  return (
    <Link
      to={`/find-a-region/${circle.slug}`}
      data-testid={`region-card-${circle.slug}`}
      className="group text-left bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-gold/60 hover:-translate-y-1 hover:shadow-xl transition-transform duration-300 flex flex-col"
    >
      <div className="relative h-48 overflow-hidden">
        <img src={regionImage(circle.slug)} alt={circle.city} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent" />
        <span className="absolute top-4 left-4 text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full bg-gold text-white shadow">Live</span>
        <div className="absolute bottom-4 left-5 right-5">
          <div className="text-white font-display text-2xl leading-tight">{circle.city}</div>
          <div className="text-white/80 text-xs mt-1">{circle.state}</div>
        </div>
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start gap-2 text-sm text-gray-600">
          <Calendar size={14} className="text-gold mt-0.5 flex-shrink-0" />
          <span>{circle.meeting_schedule || "Meeting schedule shared on visit"}</span>
        </div>
        <div className="mt-3 flex items-start gap-2 text-sm text-gray-600">
          <MapPin size={14} className="text-gold mt-0.5 flex-shrink-0" />
          <span>{circle.meeting_venue || "Venue confirmed per meeting"}</span>
        </div>
        <div className="mt-auto pt-6"><div className="pt-5 border-t border-gray-100 flex items-center gap-3">
          <OwnerAvatar circle={circle} size="w-10 h-10" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-gray-400">Region owner</div>
            <div className="text-sm font-medium text-navy truncate">{ownerDisplayName(circle)}</div>
          </div>
        </div></div>
        <div data-testid={`region-card-cta-${circle.slug}`} className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-navy group-hover:gap-2 transition-all">
          Meet the owner <ArrowRight size={14} />
        </div>
      </div>
    </Link>
  );
}

const SPAN_MD = { 0: "md:col-span-2", 1: "" };
const SPAN_LG = { 0: "lg:col-span-3", 1: "lg:col-span-2", 2: "" };

function ComingSoonCard({ liveCount }) {
  return (
    <div data-testid="region-card-coming-soon" className={`${SPAN_MD[liveCount % 2]} ${SPAN_LG[liveCount % 3]} rounded-2xl border-2 border-dashed border-gray-300 p-6 flex flex-col justify-between hover:border-gold/60 transition-colors`}>
      <div>
        <div className="overline text-gold mb-3">Coming soon</div>
        <div className="font-display text-2xl text-navy leading-tight">Your city could be next</div>
        <p className="mt-3 text-sm text-gray-600">Mumbai, Bengaluru, Pune, Hyderabad and more, we're onboarding region owners city-by-city.</p>
      </div>
      <Link to="/franchise" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-navy hover:gap-2 transition-all">Bring D32 to your city →</Link>
    </div>
  );
}

function Hero() {
  return (
    <section className="bg-navy text-white py-20 lg:py-28">
      <div className="container-narrow">
        <div className="overline text-gold mb-4">Find a D32 Region · India</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold leading-[1.05] max-w-4xl">
          Find your D32 region. <span className="italic text-gold">Meet the owner.</span>
        </h1>
        <p className="mt-6 text-lg text-gray-300 max-w-2xl">
          D32 India is currently live in <span className="text-white font-semibold">Chandigarh Tricity</span>,{" "}
          <span className="text-white font-semibold">Ahmedabad</span> and{" "}
          <span className="text-white font-semibold">Delhi-NCR</span>. Each region is led by a local owner, connect with them
          to visit a meeting or find the right membership.
        </p>
      </div>
    </section>
  );
}

function CitySelector({ circles }) {
  const [city, setCity] = useState("");
  const options = useMemo(() => {
    const names = circles.map((c) => c.city);
    return [...circles.map((c) => ({ name: c.city, live: c.is_live })),
      ...ALL_INDIA_CITIES.filter((n) => !names.includes(n)).map((name) => ({ name, live: false }))];
  }, [circles]);
  const selected = circles.find((c) => c.city === city && c.is_live);

  return (
    <section className="py-16 lg:py-20 bg-cream">
      <div className="container-narrow">
        <div className="max-w-lg">
          <label className="overline text-navy/60 mb-3 block">Or pick any city</label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="city-dropdown" className="h-14 text-base bg-white border-gray-300 focus:ring-2 focus:ring-gold">
              <SelectValue placeholder="Choose a city..." />
            </SelectTrigger>
            <SelectContent data-testid="city-dropdown-content">
              {options.map((c) => (
                <SelectItem key={c.name} value={c.name} data-testid={`city-option-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="flex items-center justify-between w-full gap-3">
                    <span>{c.name}</span>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold ${c.live ? "text-gold" : "text-gray-400"}`}>{c.live ? "Live" : "Coming Soon"}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selected && (
          <div data-testid="circle-overview" className="mt-10 bg-white border border-gray-200 rounded-2xl p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <OwnerAvatar circle={selected} size="w-16 h-16" />
              <div>
                <div className="overline text-gold mb-1">{selected.city}</div>
                <div className="font-display text-2xl text-navy">{ownerDisplayName(selected)}</div>
                <div className="text-sm text-gray-500">{selected.owner_title || "Region Owner, D32 India"}</div>
              </div>
            </div>
            <Link to={`/find-a-region/${selected.slug}`} data-testid="circle-overview-meet-owner" className="inline-flex items-center gap-2 bg-navy text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-0.5 transition-transform">
              Connect with the owner <ArrowRight size={16} />
            </Link>
          </div>
        )}
        {city && !selected && (
          <div data-testid="coming-soon-state" className="mt-10 text-center py-16 border border-dashed border-gray-300 rounded-3xl bg-white">
            <div className="text-gold overline mb-3">Launching soon</div>
            <h3 className="font-display text-3xl text-navy">{city} region is on the way</h3>
            <p className="mt-3 text-gray-600 max-w-md mx-auto">We're looking for the right local owner. Could it be you?</p>
            <Link to="/franchise" className="mt-6 inline-flex items-center gap-2 bg-navy text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-0.5 transition-transform">Become a Region Owner</Link>
          </div>
        )}
      </div>
    </section>
  );
}

export default function OurCircles() {
  const [circles, setCircles] = useState([]);
  useEffect(() => { api.get("/circles").then((res) => setCircles(res.data)); }, []);
  const live = circles.filter((c) => c.is_live);

  return (
    <div data-testid="find-region-page" className="bg-white">
      <Hero />
      <section className="py-20 lg:py-24 bg-white">
        <div className="container-narrow">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="overline text-gold mb-3">Live regions</div>
              <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight">Where D32 India is meeting</h2>
            </div>
            <div className="text-sm text-gray-500 max-w-md">Tap a region to meet its owner and get in touch directly.</div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {live.map((c) => <RegionCard key={c.id} circle={c} />)}
            <ComingSoonCard liveCount={live.length} />
          </div>
        </div>
      </section>
      <CitySelector circles={circles} />
    </div>
  );
}
